import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Target, 
  Eye, 
  Heart, 
  Lightbulb, 
  Users, 
  Award,
  CheckCircle2
} from 'lucide-react';

const coreValues = [
  {
    icon: Heart,
    title: 'Integrity',
    description: 'Transparent and ethical practices in all engagements.',
    color: 'from-red-500 to-pink-500'
  },
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'Continuous improvement and adoption of emerging technologies.',
    color: 'from-yellow-500 to-orange-500'
  },
  {
    icon: Users,
    title: 'Client-Centricity',
    description: 'Solutions tailored to each client\'s unique needs.',
    color: 'from-cyan-500 to-blue-500'
  },
  {
    icon: Award,
    title: 'Reliability',
    description: 'Consistent delivery of high-quality services.',
    color: 'from-green-500 to-emerald-500'
  }
];

const missionPoints = [
  'Provide exceptional IT services with technical expertise and proactive support',
  'Reduce downtime and improve operational efficiency through data-driven solutions',
  'Empower SMEs with intelligent, cost-effective IT solutions for digital transformation',
  'Build long-term relationships that foster growth, resilience, and operational excellence'
];

export const About = () => {
  return (
    <div className="overflow-hidden" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-fuchsia-400/10 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-4">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Pioneering <span className="gradient-text">IT Excellence</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Founded in 2023 by Kishen Sukha, ZenithForge Technology is a South Africa-based 
              IT solutions provider specializing in outsourced IT services for SMEs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-3xl blur-2xl opacity-20" />
              <img
                src="https://images.unsplash.com/photo-1686771416282-3888ddaf249b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3NzE0OTkyNzh8MA&ixlib=rb-4.1.0&q=85"
                alt="Business Partnership"
                className="relative rounded-3xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-700 rounded-full text-sm font-semibold mb-4">
                Who We Are
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Your Trusted <span className="gradient-text-alt">Technology Partner</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                ZenithForge Technology offers a robust portfolio that includes IT support and 
                maintenance, business intelligence reports, system monitoring, automation, and custom integrations.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Our mission is to deliver efficient, reliable, and scalable IT solutions that 
                empower businesses to focus on their core operations while we manage their technology needs.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full">
                  <CheckCircle2 className="text-green-500" size={18} />
                  <span className="text-sm font-medium">SME Focused</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full">
                  <CheckCircle2 className="text-green-500" size={18} />
                  <span className="text-sm font-medium">Local Expertise</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full">
                  <CheckCircle2 className="text-green-500" size={18} />
                  <span className="text-sm font-medium">Scalable Solutions</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl opacity-0 group-hover:opacity-5 transition-opacity duration-300" />
              <div className="relative bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300">
                <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 mb-6">
                  <Eye className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
                <p className="text-lg text-slate-600 leading-relaxed">
                  To be South Africa's most trusted outsourced IT partner, known for delivering 
                  innovative, efficient, and user-friendly solutions that simplify technology 
                  and empower business growth.
                </p>
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded-3xl opacity-0 group-hover:opacity-5 transition-opacity duration-300" />
              <div className="relative bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300">
                <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-purple-600 mb-6">
                  <Target className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <ul className="space-y-3">
                  {missionPoints.map((point, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2 className="text-fuchsia-500 flex-shrink-0 mt-1" size={18} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
              Our Foundation
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Core <span className="gradient-text">Values</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="value-card bg-white rounded-2xl p-8 border-2 border-slate-100 text-center hover:shadow-xl transition-all duration-300"
                data-testid={`value-card-${index}`}
              >
                <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${value.color} mb-6`}>
                  <value.icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-900">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
                Leadership
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Meet Our <span className="gradient-text">Founder</span>
              </h2>
              <h3 className="text-2xl font-semibold text-cyan-600 mb-4">Kishen Sukha</h3>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                With a passion for technology and a deep understanding of the challenges 
                faced by small and medium enterprises, Kishen founded ZenithForge Technology 
                in 2023 with a clear mission: to make enterprise-grade IT accessible to businesses of all sizes.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                His hands-on approach and commitment to client success have been instrumental 
                in establishing ZenithForge as a trusted IT partner for SMEs across South Africa.
              </p>
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 text-cyan-600 font-semibold hover:gap-3 transition-all"
              >
                Connect with Kishen <ArrowRight size={18} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl blur-2xl opacity-20" />
              <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-12 text-center">
                <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center">
                  <span className="text-4xl font-bold text-white">KS</span>
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Kishen Sukha</h4>
                <p className="text-cyan-400 mb-4">Founder & CEO</p>
                <p className="text-slate-400 text-sm">
                  "Technology should empower, not overwhelm. That's the principle behind everything we do at ZenithForge."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 cta-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Let's Build Something <span className="gradient-text">Great Together</span>
            </h2>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
              Ready to experience the ZenithForge difference? Let's discuss how we can support 
              your business growth with tailored IT solutions.
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 px-10 py-5 bg-white text-slate-900 font-bold rounded-full hover:shadow-2xl hover:shadow-white/20 transition-all duration-300"
            >
              Get in Touch
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
